import { useEffect, useRef } from 'react';
import type React from 'react';
import {
  ragApi,
  ChatMessage,
  ChatSessionListItem,
  Document,
  Project,
  RAGCitation,
} from '../../../services/api';
import { refreshChatSessionsCache } from '../../../hooks/useChatSessions';

interface UseChatConversationParams {
  initialQuery: string;
  initialDataLoaded: boolean;
  sessionId?: string;
  setSessionId: (id: string | undefined) => void;
  navigateToSession: (sessionId: string, options?: { replace?: boolean; state?: unknown }) => void;
  locationState: unknown;
  currentSessionRole: 'owner' | 'editor' | 'viewer' | null;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  input: string;
  setInput: (value: string) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  streamingMessageId: string | null;
  setStreamingMessageId: (value: string | null) => void;
  // Backend stream-phase indicator: 'retrieving' or 'generating'. Set as
  // the SSE pipeline progresses; cleared when first text token arrives or
  // on stream end. Lets the UI show a more informative label than generic
  // "Thinking…" while retrieval runs.
  setStreamStatus: (value: string | null) => void;
  sidebarCitations: RAGCitation[];
  setSidebarCitations: (citations: RAGCitation[]) => void;
  contextMode: 'auto' | 'all' | 'selected';
  selectedDocs: Document[];
  selectedChats: ChatSessionListItem[];
  selectedProjects: Project[];
  attachedFiles: File[];
  preUploadedIds?: string[];
  // Persona shapes the backend generator's audience register. Pass through
  // when the calling component has a persona signal (user profile,
  // explicit picker). Recognized: beneficiary|biller|coder|case_manager|collections.
  userPersona?: string;
  setContextLocked: (locked: boolean) => void;
  uploadStatusLines: string[];
  setUploadStatusLines: (lines: string[]) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  submittingRef: React.MutableRefObject<boolean>;
}

export function useChatConversation({
  initialQuery,
  initialDataLoaded,
  sessionId,
  setSessionId,
  navigateToSession,
  locationState,
  currentSessionRole,
  messages,
  setMessages,
  input,
  setInput,
  loading,
  setLoading,
  streamingMessageId,
  setStreamingMessageId,
  setStreamStatus,
  setSidebarCitations,
  contextMode,
  selectedDocs,
  selectedChats,
  selectedProjects,
  attachedFiles,
  preUploadedIds,
  userPersona,
  setContextLocked,
  setUploadStatusLines,
  messagesEndRef,
  submittingRef,
}: UseChatConversationParams) {
  const handleSend = async (messageText?: string) => {
    if (submittingRef.current) return;
    if (currentSessionRole === 'viewer') {
      alert('You only have viewer access to this chat. You cannot send messages.');
      return;
    }

    const text = messageText || input.trim();
    if (!text) return;

    submittingRef.current = true;
    setContextLocked(true);

    const immediateContextItems: ChatMessage['context_items'] = [
      ...selectedDocs.map((d) => ({
        id: d.id,
        context_type: 'document' as const,
        document_id: d.id,
        file_name: d.title,
        created_at: new Date().toISOString(),
      })),
      ...selectedProjects.map((p) => ({
        id: p.id,
        context_type: 'project' as const,
        project_id: p.id,
        file_name: p.name,
        created_at: new Date().toISOString(),
      })),
    ];
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      context_items: immediateContextItems.length > 0 ? immediateContextItems : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    let messagesAfterStream: ChatMessage[] = [...messages, userMessage];
    setInput('');
    setLoading(true);

    const streamingId = crypto.randomUUID();

    // Seed with any IDs that were pre-uploaded before navigation (home page → chat page)
    const uploadIds: string[] = preUploadedIds ? [...preUploadedIds] : [];
    const uploadContextItems: ChatMessage['context_items'] = [];
    const statusLines: string[] = [];
    const uploadResults = await Promise.all(
      attachedFiles.map(async (file) => {
        try {
          const result = await ragApi.upload(file);
          return { file, result, error: null as unknown };
        } catch (e) {
          console.error('Failed to upload file for RAG context:', e);
          return { file, result: null, error: e };
        }
      }),
    );
    for (const { file, result, error } of uploadResults) {
      if (error || !result) {
        statusLines.push(`⚠ Upload error for ${file.name}`);
        continue;
      }
      if (result.success && result.chunks_written > 0) {
        uploadIds.push(result.document_id);
        uploadContextItems.push({
          id: result.document_id,
          context_type: 'upload',
          document_id: result.document_id,
          file_name: file.name,
          created_at: new Date().toISOString(),
        });
        statusLines.push(`${file.name}: ${result.chunks_written} chunks indexed`);
      } else {
        statusLines.push(
          `⚠ Upload failed for ${file.name}${result.error ? ': ' + result.error : ''}`
        );
      }
    }
    if (uploadContextItems.length > 0) {
      messagesAfterStream = messagesAfterStream.map((m) =>
        m.id === userMessage.id ? { ...m, context_items: uploadContextItems } : m
      );
      setMessages(messagesAfterStream);
    }
    if (statusLines.length > 0) setUploadStatusLines(statusLines);

    const documentIds = selectedDocs.length > 0 ? selectedDocs.map((d) => d.id) : undefined;
    const projectIds = selectedProjects.length > 0 ? selectedProjects.map((p) => p.id) : undefined;
    const referencedSessionIds =
      selectedChats.length > 0 ? selectedChats.map((c) => c.id) : undefined;

    let streamingStarted = false;
    let lastStreamCitations: RAGCitation[] | undefined;
    // Defer route change for brand-new chats until the stream finishes.
    // Navigating /ask → /ask/:id mid-stream unmounts AskEmptyPage and
    // kills in-flight message state; the second prompt then "works" because
    // ChatPage is already mounted.
    const wasNewSession = !sessionId;
    let pendingSessionId: string | undefined;

    try {
      for await (const event of ragApi.chatStream(text, {
        chat_session_id: sessionId,
        upload_ids: uploadIds.length ? uploadIds : undefined,
        document_ids: documentIds,
        project_ids: projectIds,
        referenced_session_ids: referencedSessionIds,
        context_mode: contextMode,
        // Omitted: let the backend default (6) apply. Hardcoding 10 here
        // silently overrode the PR #128 / #136-#140 perf work that lowered
        // the reranker's kept-count to 6 and inflated generator input
        // tokens. The backend default is the source of truth.
        user_persona: userPersona,
      })) {
        if (event.session_id) {
          pendingSessionId = event.session_id;
          setSessionId(event.session_id);
          if (!wasNewSession) {
            navigateToSession(event.session_id, { replace: true });
          }
        }
        if (event.status) {
          // 'retrieving' or 'generating' — replaces the generic "Thinking…"
          // label with something concrete so the user doesn't think the
          // app is frozen during the retrieve+rerank phase.
          setStreamStatus(event.status);
        }
        if (event.text) {
          if (!streamingStarted) {
            streamingStarted = true;
            // First text token: status indicator becomes redundant.
            setStreamStatus(null);
            const assistantMessage: ChatMessage = {
              id: streamingId,
              role: 'assistant',
              content: event.text ?? '',
              created_at: new Date().toISOString(),
              citations: lastStreamCitations,
            };
            messagesAfterStream = [...messagesAfterStream, assistantMessage];
            setMessages(messagesAfterStream);
            setStreamingMessageId(streamingId);
          } else {
            messagesAfterStream = messagesAfterStream.map((m) =>
              m.id === streamingId ? { ...m, content: m.content + event.text } : m
            );
            setMessages(messagesAfterStream);
          }
        }
        if (event.citations) {
          lastStreamCitations = event.citations;
          setSidebarCitations(event.citations);
          if (streamingStarted) {
            messagesAfterStream = messagesAfterStream.map((m) =>
              m.id === streamingId ? { ...m, citations: event.citations } : m
            );
            setMessages(messagesAfterStream);
          }
        }
        if (event.done) break;
      }
    } catch (error) {
      console.error('RAG stream error:', error);
      const errorContent = 'Sorry, something went wrong. Try again.';
      if (streamingStarted) {
        messagesAfterStream = messagesAfterStream.map((m) =>
          m.id === streamingId ? { ...m, content: errorContent } : m
        );
        setMessages(messagesAfterStream);
      } else {
        const errorMessage: ChatMessage = {
          id: streamingId,
          role: 'assistant',
          content: errorContent,
          created_at: new Date().toISOString(),
        };
        messagesAfterStream = [...messagesAfterStream, errorMessage];
        setMessages(messagesAfterStream);
      }
    } finally {
      if (streamingStarted && lastStreamCitations?.length) {
        messagesAfterStream = messagesAfterStream.map((m) =>
          m.id === streamingId ? { ...m, citations: lastStreamCitations } : m
        );
        setMessages(messagesAfterStream);
      }
      if (wasNewSession && pendingSessionId) {
        void refreshChatSessionsCache();
        navigateToSession(pendingSessionId, {
          replace: true,
          state: {
            seedMessages: messagesAfterStream,
            seedCitations: lastStreamCitations ?? [],
          },
        });
      }
      setLoading(false);
      setStreamingMessageId(null);
      setStreamStatus(null);
      setUploadStatusLines([]);
      submittingRef.current = false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      const form = (e.target as HTMLElement).closest('form');
      form?.requestSubmit();
    }
  };

  useEffect(() => {
    if (initialDataLoaded && initialQuery && !sessionId && messages.length === 0) {
      handleSend(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoaded]);

  const prevMessageCountRef = useRef(messages.length);

  useEffect(() => {
    if (messages.length !== prevMessageCountRef.current) {
      prevMessageCountRef.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, messagesEndRef]);

  return {
    handleSend,
    handleSubmit,
    handleKeyDown,
  };
}

